import router from '@/router'
import * as getTypes from '@/store/get-types'
import * as actionTypes from '@/store/action-types'
import * as mutationTypes from '@/store/mutation-types'
import { HttpClient, TIMEOUT_ERROR_CODE } from '@/cowboy/http/HttpClient'
import {
  ORDER_COMPLETE_ROUTE,
  REDEMPTION_HISTORY_ROUTE
} from '@/router/routes/route-names'
import i18n from '@/i18n'
import { ORDER_COMPLETED } from '@/plugins/segment/SegmentConstants'
import { segmentModule } from '@/plugins/segment/SegmentModule'
import { OrderApi } from '@/cowboy/order/OrderApi'

const state = {
  isPlacingOrder: false,
  orderValidation: {
    blacklistedEmailDomains: []
  },
  orderConfirmation: null
}

const getters = {
  [getTypes.GET_BLACKLISTED_EMAIL_DOMAINS]: state =>
    state.orderValidation.blacklistedEmailDomains.map(email =>
      email.toLowerCase()
    ),
  [getTypes.GET_IS_PLACING_ORDER]: ({ isPlacingOrder }) => isPlacingOrder,
  [getTypes.GET_ORDER_CONFIRMATION]: state => state.orderConfirmation
}

const actions = {
  [actionTypes.FETCH_ORDER_VALIDATION]: async ({ commit }) => {
    const orderValidationRules = (await OrderApi.fetchOrderValidationRules()).toJSON()
    commit(mutationTypes.SET_ORDER_VALIDATION, orderValidationRules)
  },
  [actionTypes.PLACE_ORDER]: async (
    { state, commit, dispatch },
    order
  ) => {
    if (state.isPlacingOrder) return

    commit(mutationTypes.SET_IS_PLACING_ORDER, true)

    const { lineItems, externalReferenceId, generateCaptchaToken } = order || {}

    if (
      externalReferenceId &&
      Array.isArray(lineItems) &&
      lineItems.length
    ) {
      let captchaToken
      if (generateCaptchaToken && typeof generateCaptchaToken === 'function') {
        captchaToken = await generateCaptchaToken()
      }

      const resetCart = async () =>
        await dispatch(actionTypes.RESET_CART, undefined, {
          root: true
        })
      let orderResponse = null
      let isTimeoutError = false

      // Update unload events to warn user about refreshing/navigating while order
      // is being processed, and clear cart if they decide to leave anyway to avoid
      // duplicate orders and "insufficient funds" errors since the redemption is still
      // processing in the background
      const onBeforeUnload = window.onbeforeunload
      const onUnload = window.onunload
      window.onbeforeunload = () => true
      window.onunload = resetCart

      try {
        orderResponse = (await OrderApi.placeOrder(externalReferenceId, captchaToken, lineItems)).toJSON()
      } catch (error) {
        const { code } = error || {}
        let errorTitle = i18n.tc('checkout-error-title')
        let errorBody = i18n.tc('checkout-error-details')

        if (code === TIMEOUT_ERROR_CODE) {
          console.error('Call to place order timed out.')
          isTimeoutError = true
          errorTitle = i18n.tc('checkout-timeout-error-title')
          errorBody = i18n.tc('checkout-timeout-error-body')
        }

        HttpClient.handleHttpError({ errorTitle, errorBody }, error)
      } finally {
        // Restore original window events
        window.onbeforeunload = onBeforeUnload
        window.onunload = onUnload
        commit(mutationTypes.SET_IS_PLACING_ORDER, false)

        await dispatch(actionTypes.RESET_REDEMPTION_HISTORY)
        if (orderResponse) {
          await dispatch(actionTypes.PROCESS_ORDER_RESPONSE, orderResponse)
        } else if (isTimeoutError) {
          await resetCart()
          await router.push({ name: REDEMPTION_HISTORY_ROUTE })
        }
      }
    } else {
      console.error('Order attempted without required information')
      commit(mutationTypes.SET_IS_PLACING_ORDER, false)
    }
  },
  [actionTypes.PROCESS_ORDER_RESPONSE]: async (
    { dispatch, rootGetters },
    orderResponse
  ) => {
    const catalogItems = rootGetters[getTypes.GET_CATALOG_ITEMS]
    const { lineItems = [] } = orderResponse

    const orderConfirmation = {
      ...orderResponse,
      lineItems: lineItems.map(lineItem => {
        const { amountCharged, utid } = lineItem
        const { value: amount, fee } = amountCharged
        const catalogItem = catalogItems.get(utid)
        const { adjustment } = catalogItem || {}
        return {
          ...lineItem,
          amount,
          fee: {
            ...adjustment,
            cost: fee
          }
        }
      })
    }

    await dispatch(actionTypes.SET_ORDER_CONFIRMATION, orderConfirmation)

    await segmentModule.track(ORDER_COMPLETED, {
      ...rootGetters[getTypes.ORDER_TRACKING_PROPERTIES],
      ...rootGetters[getTypes.GET_LONGEST_SCROLL]
    })

    await dispatch(actionTypes.RESET_CART, undefined, {
      root: true
    })

    await dispatch(
      actionTypes.SET_CART_EXTERNAL_REF_ID,
      orderResponse.referenceOrderID,
      { root: true }
    )

    await router.push({
      name: ORDER_COMPLETE_ROUTE,
      params: { refOrderId: orderConfirmation.referenceOrderID }
    })
  },
  [actionTypes.SET_ORDER_CONFIRMATION] (context, orderConfirmation) {
    context.commit(mutationTypes.SET_ORDER_CONFIRMATION, orderConfirmation)
  }
}

const mutations = {
  [mutationTypes.SET_IS_PLACING_ORDER]: (state, isPlacingOrder) => {
    state.isPlacingOrder = Boolean(isPlacingOrder)
  },
  [mutationTypes.SET_ORDER_CONFIRMATION]: (state, orderConfirmation) => {
    state.orderConfirmation = orderConfirmation
  },
  [mutationTypes.SET_ORDER_VALIDATION]: (state, orderValidation) => {
    const { blacklistedEmailDomains } = orderValidation || {}
    state.orderValidation.blacklistedEmailDomains =
      blacklistedEmailDomains || []
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
